import React, { useEffect, useRef, useState } from "react";
import { FileSearch, Loader2, Search } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { formatItalianDate } from "../lib/dateUtils";
import { Card } from "./ui-kit";
import { DocumentStatusBadge, LotTraceLink, OfficialDocuments, documentTitle } from "./LotTrace";

/**
 * Ricerca rapida incrociata per il controllo ASL/NAS: un solo campo cerca nel
 * lotto, nella materia prima, nel fornitore, nel n. di DDT/fattura e nel lotto
 * di produzione, e per ogni lotto trovato mostra subito il documento ufficiale.
 */
export default function TraceabilitySearch({ initialQuery = "", initialMissingOnly = false, testid = "trace-search" }) {
  const [q, setQ] = useState(initialQuery);
  const [missingOnly, setMissingOnly] = useState(initialMissingOnly);
  const [state, setState] = useState({ results: null });
  const seq = useRef(0);

  const run = (text, missing) => {
    const id = ++seq.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    api.get("/app/catalog/raw-lots/trace-search", { params: { q: text, missing_document: missing || undefined } })
      .then(({ data }) => { if (id === seq.current) setState({ results: data.results, truncated: data.truncated, all: !text && !missing }); })
      .catch((e) => { if (id === seq.current) setState({ results: null, error: formatApiErrorDetail(e.response?.data?.detail) }); });
  };

  useEffect(() => {
    const text = q.trim();
    if (text.length < 2 && !missingOnly) { seq.current += 1; setState({ results: null }); return undefined; }
    const timer = window.setTimeout(() => run(text, missingOnly), 300);
    return () => window.clearTimeout(timer);
  }, [q, missingOnly]);

  // Lente verde: cerca subito; con il campo vuoto mostra tutti i lotti registrati.
  const submit = (event) => {
    event.preventDefault();
    const text = q.trim();
    if (text.length === 1) { setState({ results: null, error: "Scrivi almeno 2 caratteri, oppure svuota il campo per vedere tutti i lotti" }); return; }
    run(text, missingOnly);
  };

  const { results } = state;
  return (
    <Card className="p-5 sm:p-6" data-testid={testid}>
      <div className="flex items-start gap-3">
        <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-navy text-white"><FileSearch className="h-5 w-5" /></div>
        <div>
          <h2 className="font-heading text-xl font-bold text-navy">Ricerca rapida lotto e DDT</h2>
          <p className="mt-0.5 text-sm text-slate-500">Scrivi il lotto, la materia prima, il fornitore, il numero del DDT/fattura o il lotto di una produzione: per ogni lotto apri subito il documento ufficiale caricato.</p>
        </div>
      </div>
      <form onSubmit={submit} className="mt-4 flex gap-2" role="search">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} autoComplete="off" enterKeyHint="search"
            placeholder="Es. L2468, mozzarella, Rossi srl, DDT 154…" aria-label="Cerca lotto, materia prima, fornitore o DDT"
            className="min-h-[52px] w-full rounded-xl border-2 border-slate-200 bg-white pl-11 pr-3 text-base text-navy focus:border-navy focus:outline-none"
            data-testid={`${testid}-input`} />
        </div>
        <button type="submit" disabled={state.loading} title="Cerca (campo vuoto: mostra tutti i lotti)" aria-label="Cerca"
          className="grid min-h-[52px] w-14 shrink-0 place-items-center rounded-xl bg-emerald-600 text-white shadow-sm hover:bg-emerald-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300 disabled:opacity-70"
          data-testid={`${testid}-submit`}>
          {state.loading ? <Loader2 className="h-6 w-6 animate-spin" /> : <Search className="h-6 w-6" strokeWidth={2.5} />}
        </button>
      </form>
      <p className="mt-1.5 text-xs text-slate-400">Premi la lente verde a campo vuoto per vedere l'elenco di tutti i lotti registrati.</p>
      <label className="mt-3 inline-flex min-h-[44px] cursor-pointer items-center gap-2 text-sm text-slate-600">
        <input type="checkbox" checked={missingOnly} onChange={(e) => setMissingOnly(e.target.checked)} className="h-5 w-5 rounded border-slate-300" data-testid={`${testid}-missing`} />
        Mostra solo i lotti senza DDT/fattura (da sistemare prima di un controllo)
      </label>

      {state.error && <p className="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-700">{state.error}</p>}
      {results && results.length === 0 && (
        <p className="mt-4 rounded-lg bg-slate-50 p-4 text-sm text-slate-600" data-testid={`${testid}-empty`}>
          {missingOnly && !q.trim() ? "Tutti i lotti hanno il loro documento di acquisto allegato." : state.all ? "Non ci sono ancora lotti registrati." : "Nessun lotto trovato."}
        </p>
      )}
      {results && results.length > 0 && (
        <>
          <p className="mt-4 text-xs text-slate-500">{results.length} {state.all ? (results.length === 1 ? "lotto registrato" : "lotti registrati") : (results.length === 1 ? "lotto trovato" : "lotti trovati")}{state.truncated ? " (mostrati i più recenti: restringi la ricerca)" : ""}</p>
          <ul className="mt-2 space-y-3" data-testid={`${testid}-results`}>
            {results.map((lot) => (
              <li key={lot.lot_id} className="rounded-xl border border-hairline p-4" data-testid={`${testid}-result`}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-heading text-base font-bold text-navy">{lot.ingredient_name}</p>
                    <LotTraceLink lotId={lot.lot_id} className="font-mono text-sm font-semibold text-gold">Lotto {lot.supplier_lot || "—"}</LotTraceLink>
                  </div>
                  <DocumentStatusBadge status={lot.document_status} />
                </div>
                <p className="mt-2 text-sm text-slate-600">
                  {lot.supplier_name !== "—" ? `${lot.supplier_name} · ` : ""}ricevuto {formatItalianDate(lot.received_date)} · scad. {formatItalianDate(lot.expiry_date)}
                  {lot.receipt?.number ? ` · ${documentTitle(lot)}` : ""}
                  {lot.site_name ? ` · ${lot.site_name}` : ""}
                  {lot.productions_count ? ` · in ${lot.productions_count} ${lot.productions_count === 1 ? "produzione" : "produzioni"}` : ""}
                </p>
                {lot.matched_by?.length > 0 && <p className="mt-1 text-xs text-slate-400">Trovato per: {lot.matched_by.join(" · ")}</p>}
                <div className="mt-3"><OfficialDocuments lot={lot} /></div>
              </li>
            ))}
          </ul>
        </>
      )}
    </Card>
  );
}
